import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../../libs/Firebase'
import { Collections } from '../../../../pure-js/libs/Collections'
import { WherebyCallClaimStatus, WherebyCallKind, WherebyCallOwnershipStatus } from '../../../../pure-js/enums/Whereby'
import type {
  ClaimWherebyCallResponse,
  WherebyCallOwnershipDoc,
  WherebyCallScope
} from '../../../../pure-js/types/WherebyTypes'
import {
  WHEREBY_BLOCKED_RECLAIM_MS,
  WHEREBY_OWNERSHIP_HEARTBEAT_MS,
  buildWherebyDeviceLabel,
  resolveTakeoverResume
} from '../../../../pure-js/libs/wherebyCallOwnership'
import { useClaimWherebyCall, useReleaseWherebyCall } from '../../apiLayer'
import useAppState from '../../hooks/useAppState'
import { isMobileDevice } from '../utils/isMobileDevice'
import { broadcastCallOwnershipTaken, getWherebyTabId, subscribeToCrossTabMessages } from './wherebyCrossTab'
import { isTabSlotFree, TAB_SLOT_RELEASE_GRACE_MS, useWherebyTabSlot } from './wherebyTabSlot'
import { useGlobalWhereby } from './useGlobalWhereby'
import { useWherebyCallLost } from './useWherebyCallLost'

export interface UseWherebyCallOwnershipResult {
  status: WherebyCallOwnershipStatus
  /** Gate the Whereby connection on this — true only while this surface holds the call. */
  canConnect: boolean
  /** The user confirmed the prompt: end the other connection and continue the call here. */
  confirmTakeover: () => void
  /** Re-run the check after an ERROR. */
  retry: () => void
}

/** Only a room change needs a fresh claim; team name and the current page do not (heartbeats carry those). */
const buildScopeKey = (scope: WherebyCallScope | null): string =>
  scope ? [scope.kind, scope.teamId, scope.activityId, scope.sessionId].join('|') : ''

/**
 * Enforces ONE live Whereby connection per user, across tabs and devices.
 *
 * A surface may only connect while `canConnect` is true. When another live tab/device of the same user
 * holds the call, the status is BLOCKED and the caller shows the takeover prompt. Nothing here ends a live
 * call on its own: only `confirmTakeover` — a click by that same user — does.
 */
export const useWherebyCallOwnership = (params: {
  enabled: boolean
  scope: WherebyCallScope | null
}): UseWherebyCallOwnershipResult => {
  const { enabled, scope } = params
  const location = useLocation()
  const navigate = useNavigate()
  const { state: appState } = useAppState()
  const { startCall, setCallScope } = useGlobalWhereby()
  const handleCallLost = useWherebyCallLost()

  const userId = appState.user?.id
  const clientId = getWherebyTabId()
  const deviceLabel = useMemo(
    () => buildWherebyDeviceLabel(typeof navigator === 'undefined' ? '' : navigator.userAgent),
    []
  )

  const [status, setStatus] = useState<WherebyCallOwnershipStatus>(WherebyCallOwnershipStatus.IDLE)
  // Only used to resume the taken-over call; the prompt itself stays deliberately generic.
  const [blockedByScope, setBlockedByScope] = useState<WherebyCallScope | null>(null)
  // Bumped to re-run the claim after an error.
  const [claimAttempt, setClaimAttempt] = useState(0)

  const wantsTheCall = enabled && Boolean(userId) && Boolean(scope?.teamId)
  const hasTabSlot = useWherebyTabSlot(wantsTheCall)
  const scopeKey = buildScopeKey(scope)

  // Latest values for the interval / snapshot callbacks, so they never re-subscribe or read stale state.
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  const claimMutation = useClaimWherebyCall()
  const claimRef = useRef(claimMutation.mutateAsync)
  claimRef.current = claimMutation.mutateAsync
  const releaseMutation = useReleaseWherebyCall()
  const releaseRef = useRef(releaseMutation.mutateAsync)
  releaseRef.current = releaseMutation.mutateAsync

  // One request. Never throws: an unusable answer means "we don't know", and we then refuse to connect.
  const claimTheCall = useCallback(
    async (options?: { takeover?: boolean; scope?: WherebyCallScope }): Promise<ClaimWherebyCallResponse | null> => {
      const claimedScope = options?.scope ?? (scopeRef.current as WherebyCallScope)
      const response = await claimRef
        .current({ clientId, deviceLabel, scope: claimedScope, takeover: options?.takeover })
        .catch(() => null)
      if (!response?.success) return null
      return (response.data as ClaimWherebyCallResponse | null) ?? null
    },
    [clientId, deviceLabel]
  )

  // We lost the call to another tab/device of this user (confirmed there).
  const handleLostRef = useRef<(owner?: WherebyCallOwnershipDoc) => void>(() => {})
  handleLostRef.current = (owner?: WherebyCallOwnershipDoc) => {
    setStatus(WherebyCallOwnershipStatus.BLOCKED)
    setBlockedByScope(owner?.scope ?? null)
    handleCallLost(scopeRef.current)
  }

  // Ask the server for the call. Re-runs when the call moves to another room (the same tab keeps it — a
  // claim by the current holder is always granted) and on retry.
  useEffect(() => {
    if (!wantsTheCall) {
      setStatus(WherebyCallOwnershipStatus.IDLE)
      setBlockedByScope(null)
      return
    }
    if (!hasTabSlot) {
      setStatus(WherebyCallOwnershipStatus.BLOCKED_IN_TAB)
      setBlockedByScope(null)
      return
    }

    // Keep CONNECTED while re-claiming an already-held call, so the live connection is not torn down.
    setStatus((previous) =>
      previous === WherebyCallOwnershipStatus.CONNECTED ? previous : WherebyCallOwnershipStatus.CLAIMING
    )

    let isCancelled = false
    void claimTheCall().then((result) => {
      if (isCancelled) return
      if (!result) {
        setStatus(WherebyCallOwnershipStatus.ERROR)
        return
      }
      if (result.status === WherebyCallClaimStatus.GRANTED) {
        setBlockedByScope(null)
        setStatus(WherebyCallOwnershipStatus.CONNECTED)
        return
      }
      setBlockedByScope(result.ownership?.scope ?? null)
      setStatus(WherebyCallOwnershipStatus.BLOCKED)
    })

    return () => {
      isCancelled = true
    }
  }, [wantsTheCall, hasTabSlot, scopeKey, claimAttempt, claimTheCall])

  const isConnected = status === WherebyCallOwnershipStatus.CONNECTED

  // While blocked, quietly re-ask: the moment the other holder's heartbeat lapses (or it released), the
  // standard staleness rule grants the claim and the call connects by itself — a live holder elsewhere
  // keeps answering BLOCKED, so nothing is ever taken from a call the user is actually in.
  useEffect(() => {
    if (status !== WherebyCallOwnershipStatus.BLOCKED) return
    const interval = setInterval(() => {
      void claimTheCall().then((result) => {
        if (result?.status !== WherebyCallClaimStatus.GRANTED) return
        setBlockedByScope(null)
        setStatus(WherebyCallOwnershipStatus.CONNECTED)
      })
    }, WHEREBY_BLOCKED_RECLAIM_MS)
    return () => clearInterval(interval)
  }, [status, claimTheCall])

  // Keep the claim alive. A lapsed heartbeat is what tells another tab that this one is gone, so while we
  // are really in a call this must keep running. One request per WHEREBY_OWNERSHIP_HEARTBEAT_MS, and only
  // from the single surface that holds the call.
  useEffect(() => {
    if (!isConnected) return
    const interval = setInterval(() => {
      void claimTheCall().then((result) => {
        // A transient failure (null) keeps the call: dropping a live call over one failed ping is worse.
        if (result?.status === WherebyCallClaimStatus.BLOCKED) handleLostRef.current(result.ownership)
      })
    }, WHEREBY_OWNERSHIP_HEARTBEAT_MS)
    return () => clearInterval(interval)
  }, [isConnected, claimTheCall])

  // Same-browser fast path: the taking-over tab broadcasts, so sibling tabs drop the call immediately
  // instead of on the Firestore push — the two-live-connections overlap shrinks to milliseconds.
  // Scoped to the SAME user: a browser can hold different accounts in different tabs, and another
  // account's takeover must never end this one's call.
  useEffect(() => {
    if (!isConnected || !userId) return
    return subscribeToCrossTabMessages((message) => {
      if (message.type !== 'call-ownership-taken') return
      if (message.userId !== userId || message.newClientId === clientId) return
      handleLostRef.current()
    })
  }, [isConnected, userId, clientId])

  // Live watch of the ownership doc — the half that makes a confirmed takeover take effect on the OTHER
  // tab/device (a different laptop included) within a second, without polling.
  useEffect(() => {
    if (!isConnected || !userId) return
    return onSnapshot(
      doc(db, Collections.WHEREBY_CALL_OWNERSHIP, userId),
      (snapshot) => {
        const owner = snapshot.data() as WherebyCallOwnershipDoc | undefined
        // A missing doc means nobody owns the call (our next heartbeat re-takes it); only a DIFFERENT
        // owner evicts us.
        if (owner && owner.clientId !== clientId) handleLostRef.current(owner)
      },
      () => {
        // Read failure (offline / rules): the heartbeat response is the backstop.
      }
    )
  }, [isConnected, userId, clientId])

  // Give the claim back when this surface stops holding the call (leave, navigate away, unmount) and
  // immediately on tab close, so the next tab does not have to wait out the stale window.
  useEffect(() => {
    if (!isConnected) return
    const release = () => {
      void releaseRef.current({ clientId }).catch(() => undefined)
    }
    window.addEventListener('pagehide', release)
    return () => {
      window.removeEventListener('pagehide', release)
      // Deferred: skipped when another surface in this tab has taken the call over in the meantime.
      setTimeout(() => {
        if (isTabSlotFree()) release()
      }, TAB_SLOT_RELEASE_GRACE_MS)
    }
  }, [isConnected, clientId])

  // The user confirmed: end the other connection and continue the call here. Claimed here (not via the
  // effect) so the slot is ours BEFORE any navigation — otherwise the page we land on would prompt again.
  const confirmTakeover = useCallback(() => {
    // Moving a GAME call has to move the game too: it is the one call you cannot join from an arbitrary
    // page. We then claim the game's scope straight away, so the record describes the call we end up in.
    const resume = resolveTakeoverResume({
      previousScope: blockedByScope ?? undefined,
      currentRoute: location.pathname,
      isMobile: isMobileDevice()
    })

    setStatus(WherebyCallOwnershipStatus.CLAIMING)
    void claimTheCall({ takeover: true, scope: resume?.scope }).then((result) => {
      if (!result) {
        setStatus(WherebyCallOwnershipStatus.ERROR)
        return
      }
      if (result.status !== WherebyCallClaimStatus.GRANTED) {
        setBlockedByScope(result.ownership?.scope ?? null)
        setStatus(WherebyCallOwnershipStatus.BLOCKED)
        return
      }

      setBlockedByScope(null)
      setStatus(WherebyCallOwnershipStatus.CONNECTED)
      if (userId) broadcastCallOwnershipTaken(userId, clientId)
      if (!resume) return

      const { teamId, sprintId, activityId, activityType, sessionId } = resume.scope
      setCallScope({ kind: WherebyCallKind.GAME, teamId, sprintId, activityId, activityType, sessionId })
      startCall()
      navigate(resume.route)
    })
  }, [blockedByScope, claimTheCall, clientId, location.pathname, navigate, setCallScope, startCall, userId])

  const retry = useCallback(() => setClaimAttempt((attempt) => attempt + 1), [])

  return { status, canConnect: isConnected, confirmTakeover, retry }
}
